import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Trash2, EyeOff, ShieldCheck, HeartHandshake } from "lucide-react";
import AaryaMark from "./AaryaMark";

// Human-first, jurisdiction-neutral trust language.
// Do not add server locations, cloud provider names, or country claims here.
const SECTIONS = [
  {
    icon: Lock,
    color: "text-sage-700",
    bg: "bg-sage-100",
    title: "Encrypted, end to end of the wire",
    body: "Every byte between your phone and Aarya travels through TLS 1.3 - the same encryption banks and hospitals use. Your data is protected the moment it leaves your screen.",
  },
  {
    icon: ShieldCheck,
    color: "text-terracotta-600",
    bg: "bg-terracotta-100",
    title: "Locked when it sleeps",
    body: "When your information reaches Aarya it is encrypted at rest with AES-256, a cipher trusted by governments worldwide. Even if hardware were ever stolen, the data on it would be scrambled and useless.",
  },
  {
    icon: EyeOff,
    color: "text-ink-700",
    bg: "bg-sand-100",
    title: "What we never do",
    body: "We do not sell your data - ever, to anyone. We do not run ads, tracking pixels, or third-party analytics. We do not read your contacts, SMS, or other apps. We do not track your location in the background - GPS is captured only in the moment you tap “I am Safe” or “Need Help”.",
  },
  {
    icon: Trash2,
    color: "text-brick-500",
    bg: "bg-brick-500/10",
    title: "You can leave in one tap",
    body: "Delete your account and every trace of your data instantly, from Profile. No forms, no email exchange, no waiting period. What we hold today can be gone in the next second - because it belongs to you.",
  },
  {
    icon: HeartHandshake,
    color: "text-sage-700",
    bg: "bg-sage-100",
    title: "Real people, real accountability",
    body: "Aarya is built by IndoBlitz - a legally registered company with a physical office and a founder who reads admin@indoblitz.com personally. We answer every data-protection question within 48 hours. We follow GDPR, India’s DPDP Act 2023, and the Australian Privacy Principles.",
  },
];

export default function TrustSheet({ open, onClose }) {
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-[430px] max-h-[92dvh] bg-sand-50 rounded-t-[32px] flex flex-col"
          data-testid="trust-sheet"
        >
          <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-sand-200">
            <div className="flex items-center gap-3">
              <AaryaMark size={32}/>
              <div>
                <p className="font-display text-xl text-ink-900 leading-none">The Aarya promise</p>
                <p className="text-[10px] tracking-[0.18em] uppercase text-ink-500 mt-1">Trust, in five plain sentences</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="trust-close"
              className="h-9 w-9 rounded-full bg-white border border-sand-200 flex items-center justify-center">
              <X size={16}/>
            </button>
          </div>

          <div className="overflow-y-auto px-6 py-5 space-y-4">
            <div className="rounded-3xl bg-white border border-sand-200 p-5">
              <p className="text-[15px] text-ink-900 leading-relaxed">
                We built Aarya for our own parents. So we secured it the way we&apos;d secure a message to them.
              </p>
              <p className="text-[13px] text-ink-500 mt-2 leading-relaxed">
                Aarya asks families to share something sacred - the phone numbers of people they love. That responsibility shapes every design decision we make.
              </p>
            </div>

            {SECTIONS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="rounded-3xl bg-white border border-sand-200 p-5" data-testid={`trust-section-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-full ${s.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={18} className={s.color}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-semibold text-ink-900 leading-tight">{s.title}</p>
                      <p className="text-[13px] text-ink-600 mt-1.5 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="rounded-3xl bg-ink-900 text-white p-5">
              <p className="text-[13px] leading-relaxed opacity-90">
                Have a security question, worry, or improvement idea? Write directly to
                {" "}<a href="mailto:admin@indoblitz.com" className="underline">admin@indoblitz.com</a>.
                A real human replies. Every time.
              </p>
              <p className="text-[11px] text-white/60 mt-3 tracking-wide">- Bobby Philips, Founder · IndoBlitz</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
